export interface CalendarEvent {
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  location: string;
  organizer: string;
  url: string;
}

/**
 * Formats a date to the format required by calendar services
 */
const formatDateForCalendar = (date: Date): string => {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
};

/**
 * Generates a Google Calendar URL
 */
const generateGoogleCalendarUrl = (event: CalendarEvent): string => {
  const startDate = formatDateForCalendar(event.startDate);
  const endDate = formatDateForCalendar(event.endDate);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${startDate}/${endDate}`,
    details: event.description,
    location: event.location,
    trp: "false",
    sprop: `name:${event.organizer}&website:${event.url}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generates an Outlook Calendar URL
 */
const generateOutlookCalendarUrl = (event: CalendarEvent): string => {
  const startDate = formatDateForCalendar(event.startDate);
  const endDate = formatDateForCalendar(event.endDate);

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: startDate,
    enddt: endDate,
    body: event.description,
    location: event.location,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generates an ICS file content
 */
const generateICSContent = (event: CalendarEvent): string => {
  const startDate = formatDateForCalendar(event.startDate);
  const endDate = formatDateForCalendar(event.endDate);
  const now = formatDateForCalendar(new Date());

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Event App//Event Calendar//EN",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@event-app.com`,
    `DTSTAMP:${now}`,
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description}`,
    `LOCATION:${event.location}`,
    `ORGANIZER:CN=${event.organizer}`,
    `URL:${event.url}`,
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

/**
 * Generates a calendar URL or data URI based on the format
 */
export const generateCalendarEvent = (
  event: CalendarEvent,
  format: "google" | "outlook" | "ics"
): string => {
  switch (format) {
    case "google":
      return generateGoogleCalendarUrl(event);
    case "outlook":
      return generateOutlookCalendarUrl(event);
    case "ics":
      const icsContent = generateICSContent(event);
      return `data:text/calendar;charset=utf8,${encodeURIComponent(
        icsContent
      )}`;
    default:
      throw new Error(`Unsupported calendar format: ${format}`);
  }
};

/**
 * Utility function to create a calendar event from basic parameters
 */
export const createCalendarEvent = (
  title: string,
  description: string,
  startDate: Date,
  endDate: Date,
  location: string = "",
  organizer: string = "",
  url: string = ""
): CalendarEvent => {
  return {
    title,
    description,
    startDate,
    endDate,
    location,
    organizer,
    url,
  };
};
